import { useMemo, useState } from "react";
import { Avatar, AvatarFallback, Badge, Button, DiffViewer, Modal, PageState, SegmentedControl, Skeleton, initials, type Facet } from "@fox1ck-org/ui";
import { ResourceKind } from "@gen/tracker/v1/common_pb";

import { CardFacets } from "@/components/campaign/facets";
import { errorText } from "@/lib/errors";
import { campaignLanderIds, campaignOfferIds } from "@/components/campaign/rules";
import { useT, type TKey } from "@/lib/i18n";
import { formatStamp } from "@/lib/utils";
import { formatInt } from "@/lib/format";
import { parseDiff, useSearchAuditEvents } from "@/lib/services/audit-service";
import { useLandersByIds } from "@/lib/services/lander-service";
import { useOffersByIds } from "@/lib/services/offer-service";
import { usePeopleNames, useReportTimezone } from "@/lib/services/workspace-service";
import type { CardContext } from "./card";

/** Fields a change line names, in the words of the editor. */
const FIELD_LABEL: Record<string, TKey> = {
  name: "setup.name",
  status: "card.history.field.status",
  traffic_source_id: "setup.source.platform",
  tracking_domain_id: "card.history.field.domain",
  public_path: "setup.domain.path",
  offer_weights: "setup.offers.offer",
  lander_weights: "setup.offers.landers",
  rules: "setup.rules.title",
  cost_model: "setup.cost.title",
  default_cost: "setup.cost.rate",
  group_id: "setup.group",
  tags: "setup.tags",
  platform_params: "setup.source.pixel",
  branches: "card.history.field.branches",
  schedule: "card.history.field.schedule",
  notes: "card.history.field.notes",
};

const ACTION_LABEL: Record<string, TKey> = {
  create: "card.history.verb.create",
  update: "card.history.verb.update",
  pause: "card.history.verb.pause",
  activate: "card.history.verb.activate",
  archive: "card.history.verb.archive",
  duplicate: "card.history.verb.duplicate",
  clone: "card.history.verb.duplicate",
};

/** A campaign status word of a snapshot ("ACTIVE", "CAMPAIGN_STATUS_PAUSED") in the badge's words. */
const STATUS_WORD: Record<string, TKey> = {
  ACTIVE: "campaigns.badge.active",
  PAUSED: "campaigns.badge.paused",
  DRAFT: "campaigns.badge.draft",
  ARCHIVED: "campaigns.badge.archived",
};

/** Which group of the switch a change belongs to. */
const KINDS = ["all", "flow", "links", "status", "cost"] as const;
type Kind = (typeof KINDS)[number];
const KIND_FIELDS: Record<Exclude<Kind, "all" | "status">, string[]> = {
  flow: ["offer_weights", "lander_weights", "rules", "branches", "flow_template_id", "reentries", "offer_pin", "thank_you_lander_id"],
  links: ["tracking_domain_id", "public_path", "traffic_source_id", "platform_params"],
  cost: ["cost_model", "default_cost"],
};

/** The campaign as an audit row holds it: the request wraps it ({campaign, expected_revision}). Pure. */
function unwrap(o: Record<string, unknown> | null): Record<string, unknown> | null {
  return o && typeof o.campaign === "object" && o.campaign !== null ? (o.campaign as Record<string, unknown>) : o;
}

/** The top-level keys whose value differs between the two snapshots. Pure. */
export function changedKeys(beforeRaw: Record<string, unknown> | null, afterRaw: Record<string, unknown> | null): string[] {
  const before = unwrap(beforeRaw);
  const after = unwrap(afterRaw);
  if (!before || !after) return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...keys].filter((k) => k !== "updated_at" && k !== "revision" && JSON.stringify(before[k]) !== JSON.stringify(after[k]));
}

/** Which switch groups a change falls in, by its verb and its fields. Pure. */
export function changeKinds(verb: string, fields: string[]): Kind[] {
  const out: Kind[] = ["all"];
  if (verb === "pause" || verb === "activate" || verb === "archive" || fields.includes("status")) out.push("status");
  for (const k of ["flow", "links", "cost"] as const) if (fields.some((f) => KIND_FIELDS[k].includes(f))) out.push(k);
  return out;
}

/** The revision a row produced, when the snapshot says it. Pure. */
export function revisionOf(afterRaw: Record<string, unknown> | null): number | undefined {
  const c = unwrap(afterRaw);
  const r = Number(c?.revision ?? (afterRaw?.expected_revision !== undefined ? Number(afterRaw.expected_revision) + 1 : NaN));
  return Number.isFinite(r) && r > 0 ? r : undefined;
}

const HISTORY_PAGE = 100;

/**
 * История (доска CHistory): who changed what in this campaign, newest first,
 * from the audit log every write records — what changed as the package's
 * DiffViewer (was → is, in the editor's words and names), the revision it
 * made, and «Сравнить» for the whole snapshot. The switch narrows to the
 * flow, the links, the status or the spend; «Кто» asks the server for one
 * person's changes.
 */
export function HistoryTab({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const tz = useReportTimezone();
  const c = ctx.campaign;
  const from = (c.createdAt || "2026-01-01").slice(0, 10);
  const to = new Date().toISOString().slice(0, 10);
  const [page, setPage] = useState(0);
  const [kind, setKind] = useState<Kind>("all");
  const [actor, setActor] = useState("");
  const [compare, setCompare] = useState<{ before: Record<string, unknown>; after: Record<string, unknown>; title: string } | undefined>();
  const q = useSearchAuditEvents({
    range: { from, to },
    resourceKind: ResourceKind.CAMPAIGN,
    resourceId: c.id,
    actorId: actor || undefined,
    limit: HISTORY_PAGE,
    offset: page * HISTORY_PAGE,
  });
  const total = q.data?.total ?? 0;
  const offers = useOffersByIds(campaignOfferIds(c));
  const landers = useLandersByIds(campaignLanderIds(c));
  const people = usePeopleNames((q.data?.items ?? []).map((e) => e.actorId));
  const name = (id: string, email: string) => people.data?.[id] || email || t("card.history.system");

  const rows = useMemo(
    () =>
      (q.data?.items ?? []).map((e) => {
        const verb = e.action.split(".").pop() ?? e.action;
        const diff = parseDiff(e.beforeJson, e.afterJson);
        const fields = changedKeys(diff?.before ?? null, diff?.after ?? null);
        return { e, verb, fields, diff, kinds: changeKinds(verb, fields), revision: revisionOf(diff?.after ?? null) };
      }),
    [q.data],
  );
  const shown = rows.filter((r) => r.kinds.includes(kind));

  // Ids read as names; weights as «name 60%», the way the editor shows them.
  const labels = Object.fromEntries(Object.entries(FIELD_LABEL).map(([k, v]) => [k, t(v)]));
  const weights = (key: "offer_id" | "lander_id", get: (id: string) => string | undefined) => (value: unknown) => {
    if (!Array.isArray(value)) return "—";
    const total = value.reduce((a: number, w: { weight?: number }) => a + (Number(w.weight) || 0), 0);
    return value.map((w: Record<string, unknown>) => `${get(String(w[key])) ?? String(w[key]).slice(0, 8)} ${total ? Math.round(((Number(w.weight) || 0) / total) * 100) : 0}%`).join(", ") || "—";
  };
  const formatters: Record<string, (v: unknown) => string> = {
    offer_weights: weights("offer_id", (id) => offers.data?.get(id)?.name),
    lander_weights: weights("lander_id", (id) => landers.data?.get(id)?.name),
    rules: (v) => (Array.isArray(v) ? t("card.history.rules", { n: v.length }) : "—"),
    branches: (v) => (Array.isArray(v) ? t("card.history.branches", { n: v.length }) : "—"),
    tags: (v) => (Array.isArray(v) ? v.map((x: Record<string, unknown>) => String(x.name ?? x.id ?? "")).join(", ") || "—" : "—"),
    status: (v) => {
      const key = STATUS_WORD[String(v ?? "").replace(/^CAMPAIGN_STATUS_/, "")];
      return key ? t(key) : String(v ?? "—");
    },
  };

  const actorIds = [...new Set((q.data?.items ?? []).map((e) => e.actorId).filter(Boolean))];
  const facets: Facet[] = [
    { id: "actor", label: t("card.history.who"), kind: "options", options: actorIds.map((id) => ({ value: id, label: people.data?.[id] || id.slice(0, 8) })) },
  ];

  if (q.error) return <PageState mode="error" title={t("card.history.error")} error={errorText(q.error)} retry={() => void q.refetch()} retryLabel={t("lib.common.retry")} />;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <SegmentedControl
          size="sm"
          aria-label={t("card.history.kinds")}
          value={kind}
          onValueChange={(v: Kind) => setKind(v)}
          options={KINDS.map((k) => ({ value: k, label: t(`card.history.kind.${k}`) }))}
        />
        <CardFacets
          facets={facets}
          value={{ actor }}
          onChange={(patch) => {
            if ("actor" in patch) {
              setActor(patch.actor ?? "");
              setPage(0);
            }
          }}
        />
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground">{t("card.history.caption")}</span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border bg-card">
        {q.isLoading ? (
          <Skeleton className="m-4 h-40" />
        ) : shown.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">{t("card.history.empty")}</p>
        ) : (
          <ol className="flex flex-col px-4">
            {shown.map(({ e, verb, fields, diff, revision }) => {
              const who = name(e.actorId, e.actorEmail);
              const before = unwrap(diff?.before ?? null);
              const after = unwrap(diff?.after ?? null);
              const pick = (o: Record<string, unknown> | null) => Object.fromEntries(fields.map((f) => [f, o?.[f]]));
              return (
                <li key={e.id} className="grid grid-cols-[--spacing(30)_minmax(0,1fr)_auto] items-start gap-4 border-b border-border py-3 text-sm last:border-b-0">
                  <span className="font-mono text-xs tabular-nums text-muted-foreground">{formatStamp(e.ts, tz)}</span>
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <span className="flex items-center gap-2">
                      <Avatar className="size-6">
                        <AvatarFallback className="text-xs">{e.actorId || e.actorEmail ? initials(who) : "!"}</AvatarFallback>
                      </Avatar>
                      <b className="font-semibold">{who}</b>
                      <span className="text-muted-foreground">·</span>
                      <span>{ACTION_LABEL[verb] ? t(ACTION_LABEL[verb]) : e.action}</span>
                    </span>
                    {fields.length > 0 && before && after ? (
                      <DiffViewer left={pick(before)} right={pick(after)} labels={labels} formatters={formatters} emptyMessage="" className="pl-8" />
                    ) : null}
                  </div>
                  <span className="flex items-center gap-2">
                    {revision ? <Badge variant="outline">r{revision}</Badge> : null}
                    {before && after ? (
                      <Button variant="ghost" size="sm" onClick={() => setCompare({ before, after, title: `${who} · ${formatStamp(e.ts, tz)}` })}>
                        {t("card.history.compare")}
                      </Button>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
      {total > HISTORY_PAGE ? (
        <div className="flex shrink-0 items-center justify-end gap-2 text-xs text-muted-foreground">
          <span>{t("table.summary", { from: formatInt(page * HISTORY_PAGE + 1), to: formatInt(Math.min(total, (page + 1) * HISTORY_PAGE)), total: formatInt(total) })}</span>
          <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
            {t("table.prev")}
          </Button>
          <Button variant="secondary" size="sm" disabled={(page + 1) * HISTORY_PAGE >= total} onClick={() => setPage(page + 1)}>
            {t("table.next")}
          </Button>
        </div>
      ) : null}
      {compare ? (
        <Modal open onClose={() => setCompare(undefined)} title={t("card.history.compare.title")} description={compare.title} size="2xl">
          <DiffViewer left={compare.before} right={compare.after} mode="side-by-side" labels={labels} formatters={formatters} emptyMessage={t("card.history.compare.same")} />
        </Modal>
      ) : null}
    </div>
  );
}
